/**
 * Client-side image compression shared between the mobile PWA capture flow and
 * the desktop inventory uploader. iPhone photos come in at 3-4MB each — painful
 * on cell/wifi and often the reason a save gets tapped before uploads finish.
 *
 * Behaviour:
 *   - Auto-converts HEIC/HEIF (iPhone native format) to JPEG first so the
 *     canvas step can decode it on Chrome/Firefox/Edge (Safari handles HEIC
 *     natively but we normalise so upstream code is browser-agnostic).
 *   - Downscales to `maxEdge` (default 2048) on the longest side while preserving
 *     aspect ratio.
 *   - Re-encodes as JPEG at `quality` (default 0.82) — ~450KB per photo,
 *     visually identical for e-commerce and the AI enhance pipeline.
 *   - Falls back to the original file on ANY failure (bad image, oversized, etc)
 *     so the upload flow never breaks.
 *   - Skips non-image files (returns the input untouched).
 */
import { convertHeicToJpeg, isHeic } from "@/lib/heicConvert";
import { isMobile } from "@/lib/isMobile";

export async function compressImage(rawFile, opts = {}) {
  // On mobile, be more aggressive: smaller max edge + lower quality. iPhone
  // Safari decodes each source image to a raw pixel buffer (width × height × 4B),
  // so a 4000×3000 photo is ~48MB in RAM just for the decode. Combining a
  // smaller source (via HEIC conversion downstream, plus our own 1600-edge cap)
  // with immediate blob-ref release keeps concurrent uploads survivable.
  const mobile = isMobile();
  const { maxEdge = mobile ? 1600 : 2048, quality = mobile ? 0.78 : 0.82 } = opts;
  if (!rawFile || (!rawFile.type && !rawFile.name)) return rawFile;
  const isImageLike = (rawFile.type || "").startsWith("image/") || isHeic(rawFile);
  if (!isImageLike) return rawFile;

  // Step 1: normalise HEIC/HEIF → JPEG so the canvas step below can decode it.
  const file = isHeic(rawFile) ? await convertHeicToJpeg(rawFile) : rawFile;

  // Step 2: canvas-resize + re-encode with aggressive memory release.
  return new Promise((resolve) => {
    let url = null;
    let canvas = null;
    try {
      url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (Math.max(width, height) > maxEdge) {
          const scale = maxEdge / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        // Release the source image buffer immediately — the canvas now holds
        // the resized copy and we don't need the original decode anymore.
        img.src = "";
        if (url) { URL.revokeObjectURL(url); url = null; }
        canvas.toBlob(
          (blob) => {
            // Shrink the canvas back to nothing so the pixel buffer is GC'd.
            if (canvas) { canvas.width = 0; canvas.height = 0; canvas = null; }
            if (!blob) return resolve(file);
            resolve(new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }));
          },
          "image/jpeg",
          quality,
        );
      };
      img.onerror = () => {
        if (url) { URL.revokeObjectURL(url); url = null; }
        resolve(file);
      };
      img.src = url;
    } catch {
      if (url) URL.revokeObjectURL(url);
      resolve(file);
    }
  });
}
